import React, { useState, useEffect, useMemo } from 'react';

import { usePermissions } from '../contexts/PermissionsContext';

import {

  Typography,

  Paper,

  Button,

  Dialog,

  DialogTitle,

  DialogContent,

  DialogActions,

  TextField,

  IconButton,

  Grid,

  Snackbar,

  Alert,

  Card,

  CardContent,

  CardMedia,

  CardActions,

  CardHeader,

  Avatar,

  Box,

  Chip,

  MenuItem,

  FormControl,

  InputLabel,

  Select,

  Divider,

  InputAdornment,

  Menu,

  Tooltip,

  Badge,

  Container,

  Stack,

  Autocomplete,

  ListSubheader,

  Skeleton,

  Slider,

  Checkbox,

  FormControlLabel

} from '@mui/material';

import {

  Accordion,

  AccordionSummary,

  AccordionDetails

} from '@mui/material';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { 

  Add as AddIcon, 

  Edit as EditIcon, 

  Delete as DeleteIcon, 

  Star as StarIcon, 

  Search as SearchIcon, 

  FilterList as FilterListIcon,

  Sort as SortIcon,

  Image as ImageIcon,

  CloudUpload as CloudUploadIcon,

  Person as PersonIcon,

  MoreVert as MoreVertIcon,

  FormatQuoteRounded as FormatQuoteRoundedIcon

} from '@mui/icons-material';

import { styled, useTheme, alpha } from '@mui/material/styles';

import axios from 'axios';

import { API_URL } from '../config';



const API_BASE = API_URL.replace(/\/+$/, '');

const resolveHeadshotUrl = (headshot) => {

  if (!headshot) return '';

  if (/^(data:|blob:)/i.test(headshot)) return headshot;

  if (/^https?:\/\//i.test(headshot)) return headshot;

  const normalizedPath = headshot.startsWith('/') ? headshot : `/${headshot}`;

  return `${API_BASE}${normalizedPath}`;

};



const extractProjectId = (member, fallbackProjectId) => {

  const projectRef = member?.projectId;

  if (!projectRef) return fallbackProjectId;

  if (typeof projectRef === 'string') return projectRef;

  if (typeof projectRef === 'object') {

    if (projectRef._id) return projectRef._id;

    if (typeof projectRef.toString === 'function') return projectRef.toString();

  }

  return fallbackProjectId;

};



const getHeadshotSource = (member, fallbackProjectId) => {

  if (!member) return '';

  if (typeof member.headshot === 'string' && member.headshot.startsWith('data:image/')) {

    return member.headshot;

  }

  if (member.headshotFilePath) {

    const projectRef = extractProjectId(member, fallbackProjectId);

    if (projectRef) {

      return `/uploads/cast/${projectRef}/${member.headshotFilePath}`;

    }

  }

  return member.headshot || '';

};



const withResolvedHeadshot = (member, fallbackProjectId) => {

  if (!member) return member;

  return {

    ...member,

    headshot: resolveHeadshotUrl(getHeadshotSource(member, fallbackProjectId))

  };

};



// Styled component for file upload

const VisuallyHiddenInput = styled('input')({ 

  clip: 'rect(0 0 0 0)',

  clipPath: 'inset(50%)',

  height: 1,

  overflow: 'hidden',

  position: 'absolute',

  bottom: 0,

  left: 0,

  whiteSpace: 'nowrap',

  width: 1,

});



// Cast availability options

const AVAILABILITY_OPTIONS = [

  'Fully Available',

  'Limited Availability',

  'Weekends Only',

  'Pending Confirmation',

  'Unavailable'

];



 



// Centralized hierarchy color mapping

// 1: red, 2: blue, 3: green, 4: yellow, and so on (cycles through palette)

const getHierarchyColor = (theme, hierarchy) => {

  const paletteCycle = [

    theme.palette.error.main,    // 1 -> red

    theme.palette.info.main,     // 2 -> blue

    theme.palette.success.main,  // 3 -> green

    theme.palette.warning.main,  // 4 -> yellow

    theme.palette.secondary?.main || theme.palette.primary.main, // 5

    theme.palette.primary.main,  // 6

    theme.palette.grey[500]      // 7

  ];

  if (!hierarchy || hierarchy < 1) return theme.palette.grey[400];

  const idx = (hierarchy - 1) % paletteCycle.length;

  return paletteCycle[idx];

};



function Cast({ projectId }) {

  // Theme hook for styling

  const theme = useTheme();

  const { can } = usePermissions();

  const canView = can('cast.view');

  const canManage = can('cast.manage');

  

  // State management

  const [cast, setCast] = useState([]);

  const [open, setOpen] = useState(false);

  const [editCast, setEditCast] = useState(null);

  const [searchQuery, setSearchQuery] = useState('');

  const [filterOptions, setFilterOptions] = useState({

    hierarchy: '',

    availability: '',

    role: ''

  });

  const [sortOption, setSortOption] = useState('hierarchy');

  const [anchorEl, setAnchorEl] = useState(null);

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const [error, setError] = useState(null);

  const [imagePreview, setImagePreview] = useState(null);

  const [characterSuggestions, setCharacterSuggestions] = useState([]);

  // Local validation error for hierarchy duplicates

  const [validationError, setValidationError] = useState('');

  // Delete confirmation dialog state

  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const [castToDelete, setCastToDelete] = useState(null);

  

  // Form data with enhanced fields

  const [formData, setFormData] = useState({

    name: '',

    hierarchy: 5, // Default to minor role

    hierarchyLabel: '',

    role: '',

    storyFunction: '',

    characterName: '',

    description: '',

    headshot: '',

    headshotPosX: 50,

    headshotPosY: 50,

    availability: 'Fully Available',

    contactInfo: {

      email: '',

      phone: ''

    },

    notes: '',

    // Professional Profile

    unionStatus: 'Non-Union',

    representation: {

      agentName: '',

      agency: '',

      agentEmail: '',

      agentPhone: '',

      managerName: '',

      managerEmail: '',

      managerPhone: '',

      commissionPct: ''

    },

    location: '',

    workZone: '',

    citizenship: '',

    visaStatus: '',

    licenses: {

      passport: false,

      driversLicense: false

    },

    attributes: {

      ageRange: '',

      height: '',

      weight: '',

      sizes: { shirt: '', pants: '', shoe: '' }

    },

    contactMethods: [],

    preferredContactWindows: '',

    // Skills & Attributes

    languages: [],

    accents: [],

    specialSkills: [],

    vocalType: '',

    vocalRange: ''

  });



  useEffect(() => {

    fetchCast();

    fetchCharacterNames();

  }, [projectId]);



  // Get token for API calls

  const getToken = () => {

    return localStorage.getItem('token');

  };



  // Open delete confirm dialog

  const openDeleteConfirm = (member) => {

    setCastToDelete(member);

    setConfirmDeleteOpen(true);

  };



  // Close delete confirm dialog

  const closeDeleteConfirm = () => {

    setConfirmDeleteOpen(false);

    setCastToDelete(null);

  };



  // Perform deletion

  const confirmDelete = async () => {

    if (!castToDelete?._id) return closeDeleteConfirm();

    try {

      await axios.delete(`${API_URL}/api/v1/cast/${castToDelete._id}`, apiConfig);

      setSnackbar({ open: true, message: 'Cast member deleted', severity: 'success' });

      // Optimistic remove from local state

      setCast(prev => prev.filter(m => m._id !== castToDelete._id));

    } catch (err) {

      console.error('Error deleting cast member:', err);

      setSnackbar({ open: true, message: 'Failed to delete cast member', severity: 'error' });

    } finally {

      closeDeleteConfirm();

    }

  };



  // Local persistence for headshot positions (fallback until backend persists)

  const POS_KEY = 'castHeadshotPositions';

  const loadHeadshotPosMap = () => {

    try { return JSON.parse(localStorage.getItem(POS_KEY) || '{}'); } catch { return {}; }

  };

  const saveHeadshotPos = (id, x, y) => {

    if (!id) return;

    const map = loadHeadshotPosMap();

    map[id] = { x, y };

    localStorage.setItem(POS_KEY, JSON.stringify(map));

  };

  const getHeadshotPos = (id) => {

    const map = loadHeadshotPosMap();

    return map[id];

  };



  // API call configuration

  const apiConfig = {

    headers: {

      'Authorization': `Bearer ${getToken()}`,

      'x-auth-token': getToken(), // Adding x-auth-token for compatibility

      'Content-Type': 'application/json'

    },

    withCredentials: true // Ensure credentials are sent with cross-origin requests

  };



  const fetchCast = async () => {

    try {

      const response = await axios.get(`${API_URL}/api/v1/cast/project/${projectId}`, apiConfig);

      setCast(prev => {

        const server = Array.isArray(response.data) ? response.data : [];

        return server.map(m => {

          const normalized = withResolvedHeadshot(m, projectId);

          const prevM = prev.find(p => p._id === normalized._id);

          const ls = getHeadshotPos(normalized._id) || {};

          return {

            ...normalized,

            headshotPosX: normalized.headshotPosX ?? prevM?.headshotPosX ?? ls.x ?? 50,

            headshotPosY: normalized.headshotPosY ?? prevM?.headshotPosY ?? ls.y ?? 50

          };

        });

      });

      setError(null);

    } catch (error) {

      console.error('Error fetching cast:', error);

      setError('Failed to load cast members');

      setSnackbar({ open: true, message: 'Error fetching cast members', severity: 'error' });

    }

  };

  

  // Fetch character names from screenplay

  const fetchCharacterNames = async () => {

    try {

      const response = await axios.get(`${API_URL}/api/v1/scripts/project/${projectId}/characters`, apiConfig);

      if (response.data && response.data.characters) {

        setCharacterSuggestions(response.data.characters);

        console.log('Character suggestions from screenplay:', response.data.characters);

      }

    } catch (error) {

      console.error('Error fetching character names from screenplay:', error);

      // Don't show an error to the user, just quietly fail as this is an enhancement

    }

  };



  const handleOpen = (castMember = null) => {

    if (castMember) {

      setEditCast(castMember);

      setFormData({

        name: castMember.name,

        // Prefer new 'rank' if present; fallback to legacy 'hierarchy'

        hierarchy: (typeof castMember.rank === 'number' ? castMember.rank : castMember.hierarchy) || 5,

        hierarchyLabel: castMember.hierarchyLabel || '',

        role: castMember.role || '',

        storyFunction: castMember.storyFunction || '',

        characterName: castMember.characterName || '',

        description: castMember.description || '',

        headshot: castMember.headshot || '',

        headshotPosX: castMember.headshotPosX ?? 50,

        headshotPosY: castMember.headshotPosY ?? 50,

        availability: castMember.availability || 'Fully Available',

        contactInfo: castMember.contactInfo || { email: '', phone: '' },

        notes: castMember.notes || '',

        unionStatus: castMember.unionStatus || 'Non-Union',

        representation: castMember.representation || {

          agentName: '', agency: '', agentEmail: '', agentPhone: '',

          managerName: '', managerEmail: '', managerPhone: '', commissionPct: ''

        },

        location: castMember.location || '',

        workZone: castMember.workZone || '',

        citizenship: castMember.citizenship || '',

        visaStatus: castMember.visaStatus || '',

        licenses: castMember.licenses || { passport: false, driversLicense: false },

        attributes: castMember.attributes || { ageRange: '', height: '', weight: '', sizes: { shirt: '', pants: '', shoe: '' } },

        contactMethods: castMember.contactMethods || [],

        preferredContactWindows: castMember.preferredContactWindows || '',

        languages: castMember.languages || [],

        accents: castMember.accents || [],

        specialSkills: castMember.specialSkills || [],

        vocalType: castMember.vocalType || '',

        vocalRange: castMember.vocalRange || ''

      });

      setImagePreview(castMember.headshot || null);

    } else {

      setEditCast(null);

      setFormData({

        name: '',

        hierarchy: 5, // Default to minor role

        hierarchyLabel: '',

        role: '',

        storyFunction: '',

        characterName: '',

        description: '',

        headshot: '',

        headshotPosX: 50,

        headshotPosY: 50,

        availability: 'Fully Available',

        contactInfo: {

          email: '',

          phone: ''

        },

        notes: '',

        unionStatus: 'Non-Union',

        representation: {

          agentName: '',

          agency: '',

          agentEmail: '',

          agentPhone: '',

          managerName: '',

          managerEmail: '',

          managerPhone: '',

          commissionPct: ''

        },

        location: '',

        workZone: '',

        citizenship: '',

        visaStatus: '',

        licenses: { passport: false, driversLicense: false },

        attributes: { ageRange: '', height: '', weight: '', sizes: { shirt: '', pants: '', shoe: '' } },

        contactMethods: [],

        preferredContactWindows: '',

        languages: [],

        accents: [],

        specialSkills: [],

        vocalType: '',

        vocalRange: ''

      });

      setImagePreview(null);

    }

    setOpen(true);

    setValidationError('');

  };



  // Handle file upload for headshot

  const handleHeadshotUpload = (e) => {

    const file = e.target.files[0];

    if (file) {

      // Convert to base64 for preview and storage

      const reader = new FileReader();

      reader.onloadend = () => {

        setImagePreview(reader.result);

        setFormData(prev => ({ ...prev, headshot: reader.result }));

      };

      reader.readAsDataURL(file);

    }

  };

  

  // Handle filter menu

  const handleFilterClick = (event) => {

    setAnchorEl(event.currentTarget);

  };

  

  const handleFilterClose = () => {

    setAnchorEl(null);

  };

  

  // Handle filter changes

  const handleFilterChange = (e) => {

    const { name, value } = e.target;

    setFilterOptions(prev => ({ ...prev, [name]: value }));

  };

  

  // Handle sort change

  const handleSortChange = (e) => {

    setSortOption(e.target.value);

  };

  

  // Handle search

  const handleSearchChange = (e) => {

    setSearchQuery(e.target.value);

  };



  const handleClose = () => {

    setOpen(false);

    setEditCast(null);

  };



  // Helper: compute the next available rank number for a given role based on current cast

  // Removed Use Next helper; hierarchy is entered manually and validated for uniqueness

  // Strict options for hierarchy numbers (1–100), excluding numbers already used by others.

  const availableHierarchyOptions = useMemo(() => {

    const used = new Set(

      cast

        .map(m => (typeof m.hierarchy === 'number' ? m.hierarchy : (typeof m.rank === 'number' ? m.rank : null)))

        .filter(n => typeof n === 'number' && n >= 1)

    );

    // If editing, allow keeping the current number

    if (editCast) {

      const current = (typeof editCast.hierarchy === 'number' ? editCast.hierarchy : (typeof editCast.rank === 'number' ? editCast.rank : null));

      if (typeof current === 'number') used.delete(current);

    }

    const max = 100;

    const available = [];

    for (let i = 1; i <= max; i++) {

      if (!used.has(i)) available.push(i);

    }

    return available;

  }, [cast, editCast]);



  // Distinct hierarchy numbers present in the current cast (for filter dropdown)

  const hierarchyFilterNumbers = useMemo(() => {

    const nums = new Set(

      cast

        .map(m => (typeof m.hierarchy === 'number' ? m.hierarchy : (typeof m.rank === 'number' ? m.rank : null)))

        .filter(n => Number.isInteger(n) && n >= 1)

    );

    return Array.from(nums).sort((a, b) => a - b);

  }, [cast]);



  // Grouped Role/Function options: Suggested Leads and Supporting Archetypes + Role-only

  const roleFunctionOptions = useMemo(() => [

    // Suggested

    { group: 'Suggested', label: 'Lead — Protagonist', role: 'Lead', storyFunction: 'Protagonist' },

    { group: 'Suggested', label: 'Lead — Antagonist', role: 'Lead', storyFunction: 'Antagonist' },

    { group: 'Suggested', label: 'Secondary Lead — Deuteragonist', role: 'Secondary Lead', storyFunction: 'Deuteragonist' },

    { group: 'Suggested', label: 'Secondary Lead — Tritagonist', role: 'Secondary Lead', storyFunction: 'Tritagonist' },

    // Supporting Archetypes

    { group: 'Supporting Archetypes', label: 'Supporting — Mentor', role: 'Supporting', storyFunction: 'Mentor' },

    { group: 'Supporting Archetypes', label: 'Supporting — Love Interest', role: 'Supporting', storyFunction: 'Love Interest' },

    { group: 'Supporting Archetypes', label: 'Supporting — Comic Relief', role: 'Supporting', storyFunction: 'Comic Relief' },

    { group: 'Supporting Archetypes', label: 'Supporting — Sidekick', role: 'Supporting', storyFunction: 'Sidekick' },

    { group: 'Supporting Archetypes', label: 'Supporting — Confidant', role: 'Supporting', storyFunction: 'Confidant' },

    { group: 'Supporting Archetypes', label: 'Supporting — Voice of Reason', role: 'Supporting', storyFunction: 'Voice of Reason' },

    { group: 'Supporting Archetypes', label: 'Supporting — Guardian/Protector', role: 'Supporting', storyFunction: 'Guardian/Protector' },

    { group: 'Supporting Archetypes', label: 'Supporting — Protagonist Ally', role: 'Supporting', storyFunction: 'Protagonist Ally' },

    { group: 'Supporting Archetypes', label: 'Supporting — Antagonist Ally', role: 'Supporting', storyFunction: 'Antagonist Ally' },

    { group: 'Supporting Archetypes', label: 'Supporting — Rival', role: 'Supporting', storyFunction: 'Rival' },

    { group: 'Supporting Archetypes', label: 'Supporting — Foil', role: 'Supporting', storyFunction: 'Foil' },

    { group: 'Supporting Archetypes', label: 'Supporting — Guide', role: 'Supporting', storyFunction: 'Guide' },

    { group: 'Supporting Archetypes', label: 'Supporting — Double Agent', role: 'Supporting', storyFunction: 'Double Agent' },

    { group: 'Supporting Archetypes', label: 'Supporting — Outsider/Stranger', role: 'Supporting', storyFunction: 'Outsider/Stranger' },

    { group: 'Supporting Archetypes', label: 'Supporting — Herald/Inciting Agent', role: 'Supporting', storyFunction: 'Herald/Inciting Agent' },

    { group: 'Supporting Archetypes', label: 'Supporting — Trickster/Tempter', role: 'Supporting', storyFunction: 'Trickster/Tempter' },

    { group: 'Supporting Archetypes', label: 'Supporting — Enforcer', role: 'Supporting', storyFunction: 'Enforcer' },

    { group: 'Supporting Archetypes', label: 'Supporting — Mastermind', role: 'Supporting', storyFunction: 'Mastermind' },

    { group: 'Supporting Archetypes', label: 'Supporting — Gatekeeper', role: 'Supporting', storyFunction: 'Gatekeeper' },

    { group: 'Supporting Archetypes', label: 'Supporting — Shadow', role: 'Supporting', storyFunction: 'Shadow' },

    { group: 'Supporting Archetypes', label: 'Supporting — Skeptic', role: 'Supporting', storyFunction: 'Skeptic' },

    { group: 'Supporting Archetypes', label: 'Supporting — Bureaucrat/Authority', role: 'Supporting', storyFunction: 'Bureaucrat/Authority' },

    { group: 'Supporting Archetypes', label: 'Supporting — Rebel', role: 'Supporting', storyFunction: 'Rebel' },

    { group: 'Supporting Archetypes', label: 'Supporting — Deadpan Snarker', role: 'Supporting', storyFunction: 'Deadpan Snarker' },

    { group: 'Supporting Archetypes', label: 'Supporting — Fish-Out-of-Water', role: 'Supporting', storyFunction: 'Fish-Out-of-Water' },

    { group: 'Supporting Archetypes', label: 'Supporting — Wise Elder', role: 'Supporting', storyFunction: 'Wise Elder' },

    { group: 'Supporting Archetypes', label: 'Supporting — Ingenue', role: 'Supporting', storyFunction: 'Ingenue' },

    { group: 'Supporting Archetypes', label: 'Supporting — Wildcard', role: 'Supporting', storyFunction: 'Wildcard' },

    // Role Only

    { group: 'Role Only', label: 'Supporting', role: 'Supporting', storyFunction: '' },

    { group: 'Role Only', label: 'Minor', role: 'Minor', storyFunction: '' },

    { group: 'Role Only', label: 'Featured Extra', role: 'Featured Extra', storyFunction: '' },

    { group: 'Role Only', label: 'Background', role: 'Background', storyFunction: '' },

    { group: 'Role Only', label: 'Voice Actor', role: 'Voice', storyFunction: '' },

    { group: 'Role Only', label: 'Stunt Performer', role: 'Stunt', storyFunction: '' },

    { group: 'Role Only', label: 'Other', role: 'Other', storyFunction: '' },

  ], []);



  const handleSubmit = async (e) => {

    e.preventDefault();

    try {

      // Clear previous validation state

      setValidationError('');



      // Build payload with backward compatibility

      const rank = Number(formData.hierarchy) || 0;

      const role = formData.role || '';

      const autoLabel = role ? `${role} #${rank || '?'}` : (formData.hierarchyLabel || '');



      // Validate hierarchy number (global uniqueness, positive integer)

      if (!Number.isInteger(rank) || rank < 1) {

        const msg = 'Hierarchy must be a positive integer (1 or higher).';

        setValidationError(msg);

        setSnackbar({ open: true, message: msg, severity: 'error' });

        return;

      }

      const duplicate = cast.some(m =>

        Number(typeof m.hierarchy === 'number' ? m.hierarchy : (typeof m.rank === 'number' ? m.rank : 0)) === rank &&

        (editCast ? m._id !== editCast._id : true)

      );

      if (duplicate) {

        const msg = `Hierarchy #${rank} is already taken. Choose a different number or click Use Next.`;

        setValidationError(msg);

        setSnackbar({ open: true, message: msg, severity: 'error' });

        return;

      }



      const data = {

        ...formData,

        projectId,

        // Persist new schema fields while keeping legacy compatibility

        rank,

        hierarchy: rank,

        hierarchyLabel: formData.hierarchyLabel || autoLabel

      };

      

      if (editCast) {

        console.log('Updating cast member with PATCH request:', {

          url: `/api/v1/cast/${editCast._id}`,

          config: apiConfig,

          data: data

        });

        

        // Try the direct approach first with explicit URL

        try {

          await axios.patch(`${API_URL}/api/v1/cast/${editCast._id}`, data, apiConfig);

        } catch (patchError) {

          console.error('PATCH request failed, falling back to PUT:', patchError);

          // Fallback to PUT method which might have fewer CORS issues

          await axios.put(`${API_URL}/api/v1/cast/${editCast._id}`, data, apiConfig);

        }

        

        // Optimistically update local cast state to reflect headshot position immediately

        setCast(prev => prev.map(m =>

          m._id === editCast._id

            ? {

                ...m,

                headshot: formData.headshot,

                headshotPosX: formData.headshotPosX,

                headshotPosY: formData.headshotPosY,

                // reflect role/rank updates locally

                role,

                hierarchy: rank,

                hierarchyLabel: data.hierarchyLabel,

                rank,

                storyFunction: formData.storyFunction

              }

            : m

        ));

        setSnackbar({ open: true, message: 'Cast member updated successfully', severity: 'success' });

        // Persist locally so refresh retains the position until backend supports it

        saveHeadshotPos(editCast._id, formData.headshotPosX, formData.headshotPosY);

        handleClose();

      } else {

        const res = await axios.post(`${API_URL}/api/v1/cast`, data, apiConfig);

        const created = res?.data || {};

        // Optimistically add to local list including headshot position values

        setCast(prev => [

          {

            ...created,

            projectId,

            name: formData.name,

            hierarchy: rank,

            hierarchyLabel: data.hierarchyLabel,

            role,

            rank,

            storyFunction: formData.storyFunction,

            characterName: formData.characterName,

            description: formData.description,

            headshot: formData.headshot,

            headshotPosX: formData.headshotPosX,

            headshotPosY: formData.headshotPosY,

            availability: formData.availability,

            keyDialogue: formData.keyDialogue,

            contactInfo: formData.contactInfo,

            notes: formData.notes

          },

          ...prev

        ]);

        setSnackbar({ open: true, message: 'Cast member added successfully', severity: 'success' });

        // Persist locally for new member as well

        if (created?._id) {

          saveHeadshotPos(created._id, formData.headshotPosX, formData.headshotPosY);

        }

        handleClose();

      }

      

    } catch (error) {

      console.error('Error saving cast:', error);

      setSnackbar({ 

        open: true, 

        message: `Error ${editCast ? 'updating' : 'adding'} cast member: ${error.response?.data?.message || error.message}`,

        severity: 'error'

      });

    }

  };



  const handleDelete = async (castId) => {

    try {

      await axios.delete(`${API_URL}/api/v1/cast/${castId}`, apiConfig);

      setSnackbar({ open: true, message: 'Cast member deleted successfully', severity: 'success' });

      fetchCast();

    } catch (error) {

      console.error('Error deleting cast member:', error);

      setSnackbar({ 

        open: true, 

        message: `Error deleting cast member: ${error.response?.data?.message || error.message}`,

        severity: 'error'

      });

    }

  };



  // Reset all filters

  const resetFilters = () => {

    setFilterOptions({

      hierarchy: '',

      availability: '',

      role: ''

    });

    setSearchQuery('');

  };



  // Removed preset/custom hierarchy mode; hierarchy is a global number now



  const handleChange = (e) => {

    const { name, value } = e.target;

    if (name.startsWith('contactInfo.')) {

      const field = name.split('.')[1];

      setFormData(prev => ({

        ...prev,

        contactInfo: {

          ...prev.contactInfo,

          [field]: value

        }

      }));

    } else {

      setFormData(prev => ({

        ...prev,

        [name]: value

      }));

    }

  };

  

  // Apply filtering and sorting to the cast list

  const filteredAndSortedCast = useMemo(() => {

    let result = [...cast];

    

    // Apply search filtering

    if (searchQuery) {

      const query = searchQuery.toLowerCase();

      result = result.filter(member => 

        member.name?.toLowerCase().includes(query) ||

        member.characterName?.toLowerCase().includes(query) ||

        member.role?.toLowerCase().includes(query) ||

        member.keyDialogue?.toLowerCase().includes(query) ||

        member.description?.toLowerCase().includes(query)

      );

    }

    

    // Apply dropdown filters

    if (filterOptions.hierarchy) {

      result = result.filter(member => member.hierarchy === parseInt(filterOptions.hierarchy));

    }

    

    if (filterOptions.availability) {

      result = result.filter(member => member.availability === filterOptions.availability);

    }

    

    if (filterOptions.role) {

      result = result.filter(member => 

        member.role?.toLowerCase().includes(filterOptions.role.toLowerCase())

      );

    }

    

    // Apply sorting

    result.sort((a, b) => {

      switch (sortOption) {

        case 'hierarchy':

          return (a.hierarchy || 999) - (b.hierarchy || 999);

        case 'name':

          return a.name.localeCompare(b.name);

        case 'characterName':

          return (a.characterName || '').localeCompare(b.characterName || '');

        case 'role':

          return (a.role || '').localeCompare(b.role || '');

        case 'availability':

          return (a.availability || '').localeCompare(b.availability || '');

        default:

          return (a.hierarchy || 999) - (b.hierarchy || 999);

      }

    });

    

    return result;

  }, [cast, searchQuery, filterOptions, sortOption]);



  // Permission check - show access denied if no view permission

  if (!canView) {

    return (

      <Box sx={{ p: 3, textAlign: 'center' }}>

        <Alert severity="warning">

          You don't have permission to view cast information. Contact your project administrator for access.

        </Alert>

      </Box>

    );

  }



  return (
    <>
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        borderBottom: (theme) => `1px solid ${alpha(theme.palette.divider, 0.12)}`,
        py: 1,
        px: 2
      }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: 1,
              background: theme.palette.mode === 'dark'
                ? alpha(theme.palette.primary.main, 0.15)
                : alpha(theme.palette.primary.main, 0.1),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <PersonIcon sx={{ color: 'primary.main', fontSize: 18 }} />
          </Box>
          <Typography variant="subtitle1" sx={{
            fontWeight: 600,
            color: (theme) => theme.palette.text.primary
          }}>
            Cast
          </Typography>
          <Tooltip title="Add cast member" arrow>
            <IconButton
              onClick={() => handleOpen()}
              disabled={!canManage}
              size="small"
              sx={{
                color: (theme) => theme.palette.primary.main
              }}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>
      <Grid container spacing={3} sx={{ p: 2 }}>
          

          {/* Search and Filter Controls */}

          <Grid item xs={12}>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>

              {/* Search Bar */}

              <TextField

                placeholder="Search..."

                value={searchQuery}

                onChange={handleSearchChange}

                variant="outlined"

                size="small"

                sx={{ minWidth: 200, maxWidth: 280 }}

                InputProps={{

                  startAdornment: (

                    <InputAdornment position="start">

                      <SearchIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />

                    </InputAdornment>

                  ),

                  sx: {

                    borderRadius: 1,

                    fontSize: '0.8rem',

                    height: 32,

                    '& .MuiOutlinedInput-notchedOutline': {

                      borderColor: alpha(theme.palette.divider, 0.15)

                    }

                  }

                }}

              />

              

              {/* Sort Dropdown */}

              <FormControl size="small" sx={{ minWidth: 120 }}>

                <Select

                  value={sortOption}

                  onChange={handleSortChange}

                  displayEmpty

                  renderValue={(v) => v === 'hierarchy' ? 'Sort' : v.charAt(0).toUpperCase() + v.slice(1)}

                  sx={{

                    borderRadius: 1,

                    fontSize: '0.8rem',

                    height: 32,

                    '& .MuiOutlinedInput-notchedOutline': {

                      borderColor: alpha(theme.palette.divider, 0.15)

                    }

                  }}

                >

                  <MenuItem value="hierarchy">Hierarchy</MenuItem>

                  <MenuItem value="name">Name</MenuItem>

                  <MenuItem value="characterName">Character</MenuItem>

                  <MenuItem value="role">Role</MenuItem>

                  <MenuItem value="availability">Availability</MenuItem>

                </Select>

              </FormControl>

              

              {/* Filter Button */}

              <Tooltip title="Filter cast members">

                <IconButton 

                  onClick={handleFilterClick} 

                  size="small"

                  sx={{

                    borderRadius: 1,

                    border: `1px solid ${alpha(theme.palette.divider, 0.15)}`,

                    '&:hover': {

                      backgroundColor: 'action.hover'

                    }

                  }}

                >

                  <FilterListIcon fontSize="small" sx={{ color: 'text.secondary' }} />

                </IconButton>

              </Tooltip>

              

              {/* Reset Filters Button */}

              {(searchQuery || filterOptions.hierarchy || filterOptions.availability || filterOptions.role) && (

                <Button 

                  variant="outlined" 

                  size="small" 

                  onClick={resetFilters}

                  sx={{ 

                    borderRadius: 1,

                    fontSize: '0.8rem',

                    height: 32,

                    textTransform: 'none'

                  }}

                >

                  Reset Filters

                </Button>

              )}

              

              {/* Filter Menu */}

              <Menu

                id="filter-menu"

                anchorEl={anchorEl}

                open={Boolean(anchorEl)}

                onClose={handleFilterClose}

                PaperProps={{

                  sx: { 

                    width: 250, 

                    p: 1,

                    borderRadius: 1,

                    border: `1px solid ${alpha(theme.palette.divider, 0.12)}`

                  }

                }}

              >

                <Typography variant="subtitle2" sx={{ p: 1 }}>Filter Options</Typography>

                <Divider sx={{ mb: 1 }} />

                

                {/* Hierarchy Filter */}

                <FormControl size="small" fullWidth sx={{ mb: 2, px: 1 }}>

                  <InputLabel id="hierarchy-filter-label">Hierarchy</InputLabel>

                  <Select

                    labelId="hierarchy-filter-label"

                    name="hierarchy"

                    value={filterOptions.hierarchy}

                    label="Hierarchy"

                    onChange={handleFilterChange}

                  >

                    <MenuItem value="">All</MenuItem>

                    {hierarchyFilterNumbers.map((num) => (

                      <MenuItem key={num} value={num}>{`#${num}`}</MenuItem>

                    ))}

                  </Select>

                </FormControl>

                

                {/* Availability Filter */}

                <FormControl size="small" fullWidth sx={{ mb: 2, px: 1 }}>

                  <InputLabel id="availability-filter-label">Availability</InputLabel>

                  <Select

                    labelId="availability-filter-label"

                    name="availability"

                    value={filterOptions.availability}

                    label="Availability"

                    onChange={handleFilterChange}

                  >

                    <MenuItem value="">All</MenuItem>

                    {AVAILABILITY_OPTIONS.map((option) => (

                      <MenuItem key={option} value={option}>{option}</MenuItem>

                    ))}

                  </Select>

                </FormControl>

                

                {/* Role Filter */}

                <TextField

                  name="role"

                  label="Role Contains"

                  value={filterOptions.role}

                  onChange={handleFilterChange}

                  size="small"

                  fullWidth

                  sx={{ mb: 1, px: 1 }}

                />

              </Menu>

            </Box>

          </Grid>

          

          {error && (

            <Grid item xs={12}>

              <Alert severity="error">{error}</Alert>

            </Grid>

          )}



          {/* Cast Member Cards */}

          {filteredAndSortedCast.length > 0 ? (

            <Grid item xs={12}>

              <Grid container spacing={{ xs: 0.75, md: 1 }}>

                {filteredAndSortedCast.map((member) => (

                  <Grid item xs="auto" key={member._id}>

                    <Card 

                      elevation={0} 

                      sx={{ 

                        width: 150,

                        minHeight: 260,

                        height: '100%', 

                        display: 'flex', 

                        flexDirection: 'column',

                        borderRadius: 1,

                        overflow: 'hidden',

                        position: 'relative',

                        background: (theme) => theme.palette.mode === 'dark'

                          ? alpha('#1a1a1a', 0.9)

                          : theme.palette.background.paper,

                        border: (theme) => `1px solid ${theme.palette.mode === 'dark' ? alpha(theme.palette.divider, 0.25) : alpha(theme.palette.divider, 0.35)}`,

                        boxShadow: (theme) => theme.palette.mode === 'dark'

                          ? `0 4px 12px ${alpha(theme.palette.common.black, 0.4)}`

                          : '0 2px 8px rgba(0,0,0,0.06)',

                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',

                        transformOrigin: 'center top',

                        willChange: 'transform, box-shadow, border-color',

                        // subtle sheen overlay

                        '&::before': {

                          content: '""',

                          position: 'absolute',

                          inset: 0,

                          pointerEvents: 'none',

                          background: (theme) => `linear-gradient(145deg, ${alpha(theme.palette.common.white, theme.palette.mode === 'dark' ? 0.02 : 0.06)} 0%, transparent 55%)`

                        },

                        '&:hover': {

                          transform: 'translateY(-6px) scale(1.02)',

                          backgroundColor: (theme) => theme.palette.mode === 'dark'

                            ? alpha('#222', 0.95)

                            : theme.palette.background.paper,

                          boxShadow: (theme) => theme.palette.mode === 'dark'

                            ? `0 16px 48px ${alpha(theme.palette.common.black, 0.7)}, 0 0 0 1px ${alpha(theme.palette.common.white, 0.1)}`

                            : '0 16px 48px rgba(0,0,0,0.15)',

                          borderColor: (theme) => alpha(theme.palette.primary.main, 0.4)

                        },

                        // scale media slightly on hover for polish

                        '&:hover .cast-media': {

                          transform: 'scale(1.03)'

                        },

                        // reveal hover overlays

                        '&:hover .hover-actions': {

                          opacity: 1,

                          transform: 'translateY(0)'

                        },

                        '&:hover .glass-info': {

                          opacity: 1,

                          transform: 'translateY(0)'

                        },

                        '&:focus-within': {

                          boxShadow: (theme) => `0 0 0 3px ${alpha(theme.palette.primary.main, 0.18)}`,

                          borderColor: (theme) => alpha(theme.palette.primary.main, 0.4)

                        },

                        // respect reduced motion preference

                        '@media (prefers-reduced-motion: reduce)': {

                          transition: 'none',

                          '&:hover': { transform: 'none', boxShadow: '0 1px 6px rgba(0,0,0,0.04)' },

                          '&:hover .cast-media': { transform: 'none' }

                        }

                      }}>

                      {/* Slim accent bar for hierarchy */}

                      <Box sx={{

                        height: 3,

                        width: '100%',

                        backgroundColor: (theme) => getHierarchyColor(theme, member.hierarchy)

                      }} />



                      {/* Media area with aspect and overlay number */}

                      <Box sx={{ position: 'relative' }}>

                        <Box

                          className="cast-media"

                          sx={{

                            width: '100%',

                            height: 190,

                            bgcolor: (theme) => alpha(theme.palette.action.hover, 0.3),

                            backgroundImage: member.headshot ? `url(${member.headshot})` : 'none',

                            backgroundSize: 'cover',

                            backgroundPosition: `${member.headshotPosX ?? 50}% ${member.headshotPosY ?? 50}%`,

                            transition: 'transform 0.35s ease'

                          }}

                        >

                          {!member.headshot && (

                            <Skeleton variant="rectangular" width="100%" height="100%" sx={{ borderRadius: 0 }} />

                          )}

                        </Box>

                        {/* subtle gradient overlay for polish */}

                        <Box sx={{

                          position: 'absolute',

                          inset: 0,

                          pointerEvents: 'none',

                          background: (theme) => `linear-gradient(to bottom, ${alpha(theme.palette.common.black, 0)} 40%, ${alpha(theme.palette.common.black, 0.16)} 100%)`

                        }} />

                        {/* Hover action buttons */}

                        <Box className="hover-actions" sx={{

                          position: 'absolute',

                          top: 6,

                          right: 6,

                          display: 'flex',

                          gap: 0.5,

                          opacity: 0,

                          transform: 'translateY(-4px)',

                          transition: 'opacity 0.25s ease, transform 0.25s ease',

                          zIndex: 2

                        }}>

                          <Tooltip title={canManage ? "Edit" : "No permission to edit"} arrow>

                            <IconButton

                              aria-label="Edit cast member"

                              size="small"

                              onClick={() => handleOpen(member)}

                              disabled={!canManage}

                              sx={{

                                bgcolor: (theme) => alpha(theme.palette.background.paper, 0.6),

                                border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.3)}`,

                                
                                '&:hover': {

                                  bgcolor: (theme) => alpha(theme.palette.background.paper, 0.8)

                                }

                              }}

                            >

                              <EditIcon fontSize="small" />

                            </IconButton>

                          </Tooltip>

                          <Tooltip title={canManage ? "Delete" : "No permission to delete"} arrow>

                            <IconButton

                              aria-label="Delete cast member"

                              size="small"

                              color="error"

                              onClick={() => openDeleteConfirm(member)}

                              disabled={!canManage}

                              sx={{

                                bgcolor: (theme) => alpha(theme.palette.background.paper, 0.6),

                                border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.3)}`,

                                
                                '&:hover': {

                                  bgcolor: (theme) => alpha(theme.palette.background.paper, 0.8)

                                }

                              }}

                            >

                              <DeleteIcon fontSize="small" />

                            </IconButton>

                          </Tooltip>

                        </Box>

                        {/* Glass info bar */}

                        <Box className="glass-info" sx={{

                          position: 'absolute',

                          left: 6,

                          right: 6,

                          bottom: 6,

                          display: 'flex',

                          alignItems: 'center',

                          gap: 0.75,

                          flexWrap: 'wrap',

                          rowGap: 0.5,

                          minWidth: 0,

                          px: 1,

                          py: 0.5,

                          borderRadius: 1,

                          backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.45),

                          border: (theme) => `1px solid ${alpha(theme.palette.common.white, 0.3)}`,

                          opacity: 0,

                          transform: 'translateY(6px)',

                          transition: 'opacity 0.25s ease, transform 0.25s ease',

                          zIndex: 1

                        }}>

                          

                          {(member.role || member.storyFunction) && (

                            <Chip

                              label={member.role && member.storyFunction ? `${member.role} — ${member.storyFunction}` : (member.role || member.storyFunction)}

                              size="small"

                              sx={{

                                height: 20,

                                fontSize: '0.7rem',

                                borderRadius: '999px',

                                maxWidth: { xs: '100%', sm: '68%' },

                                '& .MuiChip-label': { px: 0.9, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },

                                bgcolor: (theme) => alpha(theme.palette.background.default, 0.5),

                                border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.3)}`,

                                color: 'text.secondary'

                              }}

                            />

                          )}

                          

                          {member.availability && (

                            <Chip

                              label={member.availability}

                              size="small"

                              sx={{

                                height: 20,

                                fontSize: '0.7rem',

                                borderRadius: '999px',

                                maxWidth: { xs: '100%', sm: '30%' },

                                '& .MuiChip-label': { px: 0.9, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },

                                bgcolor: (theme) => alpha(theme.palette.background.default, 0.5),

                                border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.3)}`,

                                color: 'text.secondary'

                              }}

                            />

                          )}

                        </Box>

                        <Box

                          sx={{

                            position: 'absolute',

                            inset: 0,

                            pointerEvents: 'none',

                            background: (theme) => `linear-gradient(to bottom, ${alpha(theme.palette.common.black, 0)} 55%, ${alpha(theme.palette.common.black, 0.06)} 100%)`

                          }} />

                        <Tooltip title="Billing/Hierarchy #" arrow>

                        <Box

                          sx={{

                            position: 'absolute',

                            top: 6,

                            left: 6,

                            width: 22,

                            height: 22,

                            borderRadius: '50%',

                            display: 'flex',

                            alignItems: 'center',

                            justifyContent: 'center',

                            fontSize: '0.7rem',

                            fontWeight: 700,

                            color: '#fff',

                            boxShadow: '0 3px 8px rgba(0,0,0,0.22)',

                            border: (theme) => `1.5px solid ${alpha(theme.palette.common.white, 0.8)}`,

                            
                            backgroundColor: (theme) => getHierarchyColor(theme, member.hierarchy)

                          }}

                        >

                          {member.hierarchy || '?'}

                        </Box>

                        </Tooltip>

                      </Box>



                      {/* Name and character */}

                      <Box sx={{ px: 0.75, pt: 0.45, pb: 0.35 }}>

                        <Typography 

                          variant="subtitle1" 

                          sx={{ 

                            fontWeight: 750,

                            fontSize: '0.86rem',

                            lineHeight: 1.2,

                            letterSpacing: 0.25,

                            overflow: 'hidden',

                            textOverflow: 'ellipsis',

                            whiteSpace: 'nowrap'

                          }}

                        >

                          {member.name}

                        </Typography>

                        <Tooltip 

                          title={`${member.characterName || 'No Character'}${member.role ? ' • ' + member.role : ''}`} 

                          arrow

                        >

                          <Typography 

                            variant="body2" 

                            color="text.secondary"

                            sx={{ 

                              mt: 0.12,

                              fontSize: '0.74rem',

                              fontStyle: 'italic',

                              letterSpacing: 0.15,

                              overflow: 'hidden',

                              textOverflow: 'ellipsis',

                              whiteSpace: 'nowrap'

                            }}

                          >

                            {(member.characterName || 'No Character')}

                            {member.role ? ` • ${member.role}` : ''}

                          </Typography>

                        </Tooltip>

                      </Box>



                      <CardContent sx={{ flexGrow: 1, p: { xs: 0.4, md: 0.6 }, position: 'relative' }}>

                        <Stack spacing={0.55}>

                          

                          {/* Key Dialogue with improved styling */}

                          {member.keyDialogue && (

                            <Box sx={{ 

                              background: (theme) => alpha(theme.palette.info.main, theme.palette.mode === 'dark' ? 0.06 : 0.05),

                              borderRadius: '8px',

                              px: 1.25,

                              py: 1,

                              borderLeft: (theme) => `3px solid ${alpha(theme.palette.info.main, 0.8)}`,

                              display: 'flex',

                              gap: 1

                            }}>

                              <FormatQuoteRoundedIcon sx={{ fontSize: 16, color: 'text.secondary', opacity: 0.6, mt: 0.5 }} />

                              <Box sx={{ flex: 1 }}>

                                <Typography 

                                  variant="subtitle2" 

                                  color="text.secondary" 

                                  sx={{ 

                                    mb: 0.5, 

                                    fontSize: '0.75rem', 

                                    textTransform: 'uppercase', 

                                    letterSpacing: 0.5,

                                    fontWeight: 600

                                  }}

                                >

                                  Key Dialogue

                                </Typography>

                                <Typography variant="body2" sx={{ 

                                  fontStyle: 'italic', 

                                  color: 'text.primary',

                                  display: '-webkit-box',

                                  WebkitLineClamp: 2,

                                  WebkitBoxOrient: 'vertical',

                                  overflow: 'hidden',

                                  fontSize: '0.875rem',

                                  lineHeight: 1.5

                                }}>

                                  "{member.keyDialogue}"

                                </Typography>

                              </Box>

                            </Box>

                          )}

                          

                          {/* Description - show only if no key dialogue */}

                          {member.description && !member.keyDialogue && (

                            <Box sx={{ 

                              background: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',

                              borderRadius: '8px',

                              p: 1

                            }}>

                              <Typography 

                                variant="subtitle2" 

                                color="text.secondary" 

                                sx={{ 

                                  mb: 0.5, 

                                  fontSize: '0.75rem', 

                                  textTransform: 'uppercase', 

                                  letterSpacing: 0.5,

                                  fontWeight: 600

                                }}

                              >

                                Description

                              </Typography>

                              <Typography variant="body2" sx={{ 

                                display: '-webkit-box',

                                WebkitLineClamp: 2,

                                WebkitBoxOrient: 'vertical',

                                overflow: 'hidden',

                                fontSize: '0.875rem',

                                lineHeight: 1.5

                              }}>

                                {member.description}

                              </Typography>

                            </Box>

                          )}

                          

                          {/* Contact Info with improved layout */}

                          {(member.contactInfo?.email || member.contactInfo?.phone) && (

                            <Box sx={{ mt: 'auto' }}>

                              <Typography 

                                variant="subtitle2" 

                                color="text.secondary" 

                                sx={{ 

                                  mb: 0.5, 

                                  fontSize: '0.75rem', 

                                  textTransform: 'uppercase', 

                                  letterSpacing: 0.5,

                                  fontWeight: 600

                                }}

                              >

                                Contact

                              </Typography>

                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>

                                {member.contactInfo?.email && (

                                  <Chip 

                                    label={member.contactInfo.email} 

                                    size="small"

                                    sx={{ 

                                      height: 22,

                                      maxWidth: '100%',

                                      borderRadius: '999px',

                                      backgroundColor: (theme) => alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.12 : 0.06),

                                      color: 'text.secondary',

                                      border: 'none',

                                      '.MuiChip-label': { 

                                        overflow: 'hidden',

                                        textOverflow: 'ellipsis',

                                        whiteSpace: 'nowrap',

                                        px: 1.0

                                      } 

                                    }}

                                  />

                                )}

                                {member.contactInfo?.phone && (

                                  <Chip 

                                    label={member.contactInfo.phone} 

                                    size="small"

                                    sx={{ 

                                      height: 22,

                                      borderRadius: '999px',

                                      backgroundColor: (theme) => alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.12 : 0.06),

                                      color: 'text.secondary',

                                      border: 'none',

                                      '.MuiChip-label': { px: 1.0 }

                                    }}

                                  />

                                )}

                              </Box>

                            </Box>

                          )}

                        </Stack>

                      </CardContent>

                    </Card>

                  </Grid>

                ))}

              </Grid>

            </Grid>

          ) : (

            <Grid item xs={12}>

              <Paper elevation={0} sx={{ 

                p: 3, 

                textAlign: 'center', 

                borderRadius: 1,

                bgcolor: (theme) => theme.palette.mode === 'dark'

                  ? alpha('#1a1a1a', 0.9)

                  : 'background.default',

                border: (theme) => theme.palette.mode === 'dark'

                  ? `1px solid ${alpha(theme.palette.divider, 0.25)}`

                  : `1px solid ${alpha(theme.palette.divider, 0.1)}`,

                boxShadow: (theme) => theme.palette.mode === 'dark'

                  ? `0 4px 12px ${alpha(theme.palette.common.black, 0.4)}`

                  : '0 2px 8px rgba(0,0,0,0.06)'

              }}>

                <Typography variant="h6" color="text.secondary">

                  {searchQuery || filterOptions.hierarchy || filterOptions.availability || filterOptions.role

                    ? 'No cast members match your filters' 

                    : 'No cast members found'}

                </Typography>

                {searchQuery || filterOptions.hierarchy || filterOptions.availability || filterOptions.role ? (

                  <Button 

                    variant="outlined" 

                    sx={{ mt: 2 }} 

                    onClick={resetFilters}

                  >

                    Reset Filters

                  </Button>

                ) : (

                  <Button 

                    variant="contained" 

                    startIcon={<AddIcon />} 

                    sx={{ mt: 2 }} 

                    onClick={() => handleOpen()}

                  >

                    Add your first cast member

                  </Button>

                )}

              </Paper>

            </Grid>

          )}

        </Grid>
      </Box>

      <Dialog 

        open={open} 

        onClose={handleClose} 

        maxWidth="md" 

        fullWidth

        PaperProps={{

          sx: {

            borderRadius: 1,

            border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,

            overflow: 'hidden'

          }

        }}

      >

        <DialogTitle sx={{

          fontWeight: 600,

          display: 'flex',

          alignItems: 'center',

          gap: 1,

          borderBottom: '1px solid',

          borderColor: 'divider',

          pb: 2

        }}>

          {editCast ? 'Edit Cast Member' : 'Add New Cast Member'}

        </DialogTitle>

        <DialogContent dividers>

          <Grid container spacing={3}>

            {/* Left column - Basic details */}

            <Grid item xs={12} md={6}>

              <Typography variant="subtitle1" fontWeight="500" gutterBottom>

                Basic Information

              </Typography>

              

              <Grid container spacing={2}>

                <Grid item xs={12}>

                  <TextField

                    name="name"

                    label="Actor/Actress Name"

                    value={formData.name}

                    onChange={handleChange}

                    fullWidth

                    required

                    sx={{

                      '& .MuiOutlinedInput-root': {

                        
                        backgroundColor: alpha(theme.palette.background.paper, 0.5),

                        borderRadius: 1,

                        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,

                        transition: 'all 0.3s ease-in-out',

                        '&:hover': {

                          backgroundColor: alpha(theme.palette.background.paper, 0.7)

                        },

                        '&.Mui-focused': {

                          borderColor: alpha(theme.palette.primary.main, 0.3)

                        }

                      }

                    }}

                  />

                </Grid>

                

                <Grid item xs={12}>

                  <Autocomplete

                    id="character-name-autocomplete"

                    freeSolo

                    fullWidth

                    value={formData.characterName || null}

                    onChange={(event, newValue) => {

                      setFormData(prev => ({

                        ...prev,

                        characterName: newValue || ''

                      }));

                    }}

                    inputValue={formData.characterName || ''}

                    onInputChange={(event, newInputValue) => {

                      setFormData(prev => ({

                        ...prev,

                        characterName: newInputValue

                      }));

                    }}

                    options={[

                      ...characterSuggestions,

                      ...Array.from(new Set(cast.map(member => member.characterName)))

                        .filter(name => name && !characterSuggestions.includes(name))

                    ].filter(Boolean).sort()}

                    groupBy={(option) => {

                      if (characterSuggestions.includes(option)) {

                        return 'Characters from Screenplay';

                      } else {

                        return 'Previously Used Characters';

                      }

                    }}

                    renderInput={(params) => (

                      <TextField

                        {...params}

                        label="Character Name"

                        placeholder="Type or select a character name"

                        required

                        sx={{

                          '& .MuiOutlinedInput-root': {

                            
                            backgroundColor: alpha(theme.palette.background.paper, 0.5),

                            borderRadius: 1,

                            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,

                            transition: 'all 0.3s ease-in-out',

                            '&:hover': {

                              backgroundColor: alpha(theme.palette.background.paper, 0.7),

                              boxShadow: `0 4px 12px 0 ${alpha(theme.palette.common.black, 0.08)}`

                            },

                            '&.Mui-focused': {

                              boxShadow: `0 4px 12px 0 ${alpha(theme.palette.primary.main, 0.15)}`,

                              borderColor: alpha(theme.palette.primary.main, 0.3)

                            }

                          }

                        }}

                      />

                    )}

                    ListboxProps={{

                      style: {

                        maxHeight: '300px'

                      }

                    }}

                  />

                </Grid>

                

                <Grid item xs={12}>

                  <FormControl fullWidth>

                    <Autocomplete

                      id="hierarchy-number-autocomplete"

                      options={availableHierarchyOptions}

                      value={Number.isInteger(Number(formData.hierarchy)) && Number(formData.hierarchy) >= 1 ? Number(formData.hierarchy) : null}

                      onChange={(event, newValue) => {

                        setFormData(prev => {

                          const nextHierarchy = typeof newValue === 'number' ? newValue : 0;

                          // Auto-set defaults when selecting #1 and role/storyFunction are empty

                          if (nextHierarchy === 1 && !prev.role && !prev.storyFunction) {

                            return { ...prev, hierarchy: nextHierarchy, role: 'Lead', storyFunction: 'Protagonist' };

                          }

                          return { ...prev, hierarchy: nextHierarchy };

                        });

                        setValidationError('');

                      }}

                      getOptionLabel={(option) => String(option)}

                      isOptionEqualToValue={(option, value) => option === value}

                      renderInput={(params) => (

                        <TextField

                          {...params}

                          label="Hierarchy Number"

                          placeholder="Choose a number"

                          sx={{

                            '& .MuiOutlinedInput-root': {

                              
                              backgroundColor: alpha(theme.palette.background.paper, 0.5),

                              borderRadius: 1,

                              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,

                            }

                          }}

                        />

                      )}

                    />

                  </FormControl>

                  {/* Helper removed per request; hierarchy remains a simple unique number */}

                  {!!validationError && (

                    <Typography variant="caption" color="error" sx={{ mt: 0.75, display: 'block' }}>

                      {validationError}

                    </Typography>

                  )}

                </Grid>

                

                <Grid item xs={12}>

                  <Autocomplete

                    id="role-function-autocomplete"

                    fullWidth

                    options={roleFunctionOptions}

                    groupBy={(option) => option.group}

                    getOptionLabel={(option) => option.label}

                    isOptionEqualToValue={(opt, val) => opt.role === val.role && (opt.storyFunction || '') === (val.storyFunction || '')}

                    value={roleFunctionOptions.find(o => o.role === (formData.role || '') && (o.storyFunction || '') === (formData.storyFunction || '')) || null}

                    onChange={(event, newValue) => {

                      setFormData(prev => ({

                        ...prev,

                        role: newValue?.role || '',

                        storyFunction: newValue?.storyFunction || ''

                      }));

                    }}

                    renderInput={(params) => (

                      <TextField

                        {...params}

                        label="Role (Type and Story Function)"

                        placeholder="Select role and function"

                        sx={{

                          '& .MuiOutlinedInput-root': {

                            
                            backgroundColor: alpha(theme.palette.background.paper, 0.5),

                            borderRadius: 1,

                            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,

                          }

                        }}

                      />

                    )}

                  />

                </Grid>

                

                <Grid item xs={12}>

                  <FormControl fullWidth>

                    <InputLabel id="availability-label">Availability</InputLabel>

                    <Select

                      labelId="availability-label"

                      name="availability"

                      value={formData.availability}

                      label="Availability"

                      onChange={handleChange}

                      sx={{

                        
                        backgroundColor: alpha(theme.palette.background.paper, 0.5),

                        borderRadius: 1,

                        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,

                        transition: 'all 0.3s ease-in-out',

                        '&:hover': {

                          backgroundColor: alpha(theme.palette.background.paper, 0.7)

                        },

                        '&.Mui-focused': {

                          borderColor: alpha(theme.palette.primary.main, 0.3)

                        }

                      }}

                    >

                      <MenuItem value="Fully Available">Fully Available</MenuItem>

                      <MenuItem value="Limited Availability">Limited Availability</MenuItem>

                      <MenuItem value="Weekends Only">Weekends Only</MenuItem>

                      <MenuItem value="Evenings Only">Evenings Only</MenuItem>

                      <MenuItem value="Custom Schedule">Custom Schedule</MenuItem>

                      <MenuItem value="Unavailable">Unavailable</MenuItem>

                    </Select>

                  </FormControl>

                </Grid>

                

                {/* Professional Profile (moved from right column) */}

                <Grid item xs={12}>

                  <Accordion elevation={0} defaultExpanded={false} sx={{

                    borderRadius: 1,

                    border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,

                    backgroundColor: alpha(theme.palette.background.paper, 0.5)

                  }}>

                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>

                      <Typography variant="subtitle1" fontWeight={600}>Professional Profile</Typography>

                    </AccordionSummary>

                    <AccordionDetails>

                      <Grid container spacing={2}>

                        <Grid item xs={12} sm={6}>

                          <FormControl fullWidth>

                            <InputLabel id="union-status-label">Union Status</InputLabel>

                            <Select

                              labelId="union-status-label"

                              name="unionStatus"

                              value={formData.unionStatus}

                              label="Union Status"

                              onChange={handleChange}

                              sx={{

                                
                                backgroundColor: alpha(theme.palette.background.paper, 0.5),

                                borderRadius: 1,

                                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,

                              }}

                            >

                              <MenuItem value="SAG-AFTRA">SAG-AFTRA</MenuItem>

                              <MenuItem value="Equity">Equity</MenuItem>

                              <MenuItem value="Non-Union">Non-Union</MenuItem>

                            </Select>

                          </FormControl>

                        </Grid>

                        <Grid item xs={12} sm={6}>

                          <TextField

                            name="location"

                            label="Location (City, State/Country)"

                            value={formData.location}

                            onChange={handleChange}

                            fullWidth

                          />

                        </Grid>

                        <Grid item xs={12} sm={6}>

                          <TextField

                            name="workZone"

                            label="Work Zone / Local Hire"

                            value={formData.workZone}

                            onChange={handleChange}

                            fullWidth

                          />

                        </Grid>

                        <Grid item xs={12} sm={6}>

                          <TextField

                            name="citizenship"

                            label="Citizenship"

                            value={formData.citizenship}

                            onChange={handleChange}

                            fullWidth

                          />

                        </Grid>

                        <Grid item xs={12} sm={6}>

                          <TextField

                            name="visaStatus"

                            label="Visa Status"

                            value={formData.visaStatus}

                            onChange={handleChange}

                            fullWidth

                          />

                        </Grid>

                        <Grid item xs={12} sm={6}>

                          <FormControlLabel

                            control={

                              <Checkbox

                                checked={Boolean(formData.licenses?.passport)}

                                onChange={(e) => setFormData(prev => ({

                                  ...prev,

                                  licenses: { ...prev.licenses, passport: e.target.checked }

                                }))}

                              />

                            }

                            label="Valid Passport"

                          />

                          <FormControlLabel

                            control={

                              <Checkbox

                                checked={Boolean(formData.licenses?.driversLicense)}

                                onChange={(e) => setFormData(prev => ({

                                  ...prev,

                                  licenses: { ...prev.licenses, driversLicense: e.target.checked }

                                }))}

                              />

                            }

                            label="Driver's License"

                          />

                        </Grid>

                        {/* Representation */}

                        <Grid item xs={12}>

                          <Typography variant="subtitle2" sx={{ mt: 1 }}>Representation</Typography>

                        </Grid>

                        <Grid item xs={12} sm={6}>

                          <TextField

                            label="Agent Name"

                            value={formData.representation?.agentName || ''}

                            onChange={(e) => setFormData(prev => ({

                              ...prev,

                              representation: { ...prev.representation, agentName: e.target.value }

                            }))}

                            fullWidth

                          />

                        </Grid>

                        <Grid item xs={12} sm={6}>

                          <TextField

                            label="Agency"

                            value={formData.representation?.agency || ''}

                            onChange={(e) => setFormData(prev => ({

                              ...prev,

                              representation: { ...prev.representation, agency: e.target.value }

                            }))}

                            fullWidth

                          />

                        </Grid>

                        <Grid item xs={12} sm={6}>

                          <TextField

                            label="Agent Email"

                            type="email"

                            value={formData.representation?.agentEmail || ''}

                            onChange={(e) => setFormData(prev => ({

                              ...prev,

                              representation: { ...prev.representation, agentEmail: e.target.value }

                            }))}

                            fullWidth

                          />

                        </Grid>

                        <Grid item xs={12} sm={6}>

                          <TextField

                            label="Agent Phone"

                            value={formData.representation?.agentPhone || ''}

                            onChange={(e) => setFormData(prev => ({

                              ...prev,

                              representation: { ...prev.representation, agentPhone: e.target.value }

                            }))}

                            fullWidth

                          />

                        </Grid>

                        <Grid item xs={12} sm={6}>

                          <TextField

                            label="Manager Name"

                            value={formData.representation?.managerName || ''}

                            onChange={(e) => setFormData(prev => ({

                              ...prev,

                              representation: { ...prev.representation, managerName: e.target.value }

                            }))}

                            fullWidth

                          />

                        </Grid>

                        <Grid item xs={12} sm={6}>

                          <TextField

                            label="Manager Email"

                            type="email"

                            value={formData.representation?.managerEmail || ''}

                            onChange={(e) => setFormData(prev => ({

                              ...prev,

                              representation: { ...prev.representation, managerEmail: e.target.value }

                            }))}

                            fullWidth

                          />

                        </Grid>

                        <Grid item xs={12} sm={6}>

                          <TextField

                            label="Manager Phone"

                            value={formData.representation?.managerPhone || ''}

                            onChange={(e) => setFormData(prev => ({

                              ...prev,

                              representation: { ...prev.representation, managerPhone: e.target.value }

                            }))}

                            fullWidth

                          />

                        </Grid>

                        <Grid item xs={12} sm={6}>

                          <TextField

                            label="Commission %"

                            type="number"

                            inputProps={{ min: 0, max: 100, step: 0.5 }}

                            value={formData.representation?.commissionPct || ''}

                            onChange={(e) => setFormData(prev => ({

                              ...prev,

                              representation: { ...prev.representation, commissionPct: e.target.value }

                            }))}

                            fullWidth

                          />

                        </Grid>

                        {/* Physical Attributes */}

                        <Grid item xs={12}>

                          <Typography variant="subtitle2" sx={{ mt: 1 }}>Attributes</Typography>

                        </Grid>

                        <Grid item xs={12} sm={4}>

                          <TextField

                            label="Age Range"

                            value={formData.attributes?.ageRange || ''}

                            onChange={(e) => setFormData(prev => ({

                              ...prev,

                              attributes: { ...prev.attributes, ageRange: e.target.value }

                            }))}

                            fullWidth

                          />

                        </Grid>

                        <Grid item xs={12} sm={4}>

                          <TextField

                            label="Height (ft/in or cm)"

                            value={formData.attributes?.height || ''}

                            onChange={(e) => setFormData(prev => ({

                              ...prev,

                              attributes: { ...prev.attributes, height: e.target.value }

                            }))}

                            fullWidth

                          />

                        </Grid>

                        <Grid item xs={12} sm={4}>

                          <TextField

                            label="Weight (lb or kg)"

                            value={formData.attributes?.weight || ''}

                            onChange={(e) => setFormData(prev => ({

                              ...prev,

                              attributes: { ...prev.attributes, weight: e.target.value }

                            }))}

                            fullWidth

                          />

                        </Grid>

                        <Grid item xs={12} sm={4}>

                          <TextField

                            label="Shirt Size"

                            value={formData.attributes?.sizes?.shirt || ''}

                            onChange={(e) => setFormData(prev => ({

                              ...prev,

                              attributes: { ...prev.attributes, sizes: { ...prev.attributes.sizes, shirt: e.target.value } }

                            }))}

                            fullWidth

                          />

                        </Grid>

                        <Grid item xs={12} sm={4}>

                          <TextField

                            label="Pants Size"

                            value={formData.attributes?.sizes?.pants || ''}

                            onChange={(e) => setFormData(prev => ({

                              ...prev,

                              attributes: { ...prev.attributes, sizes: { ...prev.attributes.sizes, pants: e.target.value } }

                            }))}

                            fullWidth

                          />

                        </Grid>

                        <Grid item xs={12} sm={4}>

                          <TextField

                            label="Shoe Size"

                            value={formData.attributes?.sizes?.shoe || ''}

                            onChange={(e) => setFormData(prev => ({

                              ...prev,

                              attributes: { ...prev.attributes, sizes: { ...prev.attributes.sizes, shoe: e.target.value } }

                            }))}

                            fullWidth

                          />

                        </Grid>

                        {/* Contact Methods */}

                        <Grid item xs={12}>

                          <Autocomplete

                            multiple

                            freeSolo

                            options={[

                              'Email', 'Phone', 'Text', 'WhatsApp', 'Signal', 'Telegram'

                            ]}

                            value={formData.contactMethods}

                            onChange={(e, value) => setFormData(prev => ({ ...prev, contactMethods: value }))}

                            renderInput={(params) => (

                              <TextField {...params} label="Contact Methods" placeholder="Add method" />

                            )}

                          />

                        </Grid>

                        <Grid item xs={12}>

                          <TextField

                            name="preferredContactWindows"

                            label="Preferred Contact Windows (e.g., Weekdays 10am–6pm PT)"

                            value={formData.preferredContactWindows}

                            onChange={handleChange}

                            fullWidth

                          />

                        </Grid>

                      </Grid>

                    </AccordionDetails>

                  </Accordion>

                </Grid>



                {/* Skills & Attributes (moved from right column) */}

                <Grid item xs={12}>

                  <Accordion elevation={0} defaultExpanded={false} sx={{

                    borderRadius: 1,

                    border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,

                    backgroundColor: alpha(theme.palette.background.paper, 0.5)

                  }}>

                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>

                      <Typography variant="subtitle1" fontWeight={600}>Skills & Attributes</Typography>

                    </AccordionSummary>

                    <AccordionDetails>

                      <Grid container spacing={2}>

                        <Grid item xs={12}>

                          <Autocomplete

                            multiple

                            freeSolo

                            options={[

                              'English', 'Spanish', 'French', 'German', 'Mandarin', 'Cantonese', 'Hindi', 'Arabic', 'Portuguese', 'Russian'

                            ]}

                            value={formData.languages}

                            onChange={(e, value) => setFormData(prev => ({ ...prev, languages: value }))}

                            renderInput={(params) => (

                              <TextField {...params} label="Languages" placeholder="Add language" />

                            )}

                          />

                        </Grid>

                        <Grid item xs={12}>

                          <Autocomplete

                            multiple

                            freeSolo

                            options={[

                              'American', 'British', 'Australian', 'Irish', 'Scottish', 'Southern US', 'New York', 'French', 'German', 'Russian'

                            ]}

                            value={formData.accents}

                            onChange={(e, value) => setFormData(prev => ({ ...prev, accents: value }))}

                            renderInput={(params) => (

                              <TextField {...params} label="Accents" placeholder="Add accent" />

                            )}

                          />

                        </Grid>

                        <Grid item xs={12}>

                          <Autocomplete

                            multiple

                            freeSolo

                            options={[

                              'Stage Combat', 'Firearms Safety', 'Dance', 'Improvisation', 'Athletics', 'Driving', 'Swimming', 'Horseback Riding', 'Singing', 'Musical Instrument'

                            ]}

                            value={formData.specialSkills}

                            onChange={(e, value) => setFormData(prev => ({ ...prev, specialSkills: value }))}

                            renderInput={(params) => (

                              <TextField {...params} label="Special Skills" placeholder="Add skill" />

                            )}

                          />

                        </Grid>

                        <Grid item xs={12} sm={6}>

                          <FormControl fullWidth>

                            <InputLabel id="vocal-type-label">Vocal Type</InputLabel>

                            <Select

                              labelId="vocal-type-label"

                              value={formData.vocalType || ''}

                              label="Vocal Type"

                              onChange={(e) => setFormData(prev => ({ ...prev, vocalType: e.target.value }))}

                            >

                              <MenuItem value="">None</MenuItem>

                              <MenuItem value="Soprano">Soprano</MenuItem>

                              <MenuItem value="Mezzo-Soprano">Mezzo-Soprano</MenuItem>

                              <MenuItem value="Alto">Alto</MenuItem>

                              <MenuItem value="Tenor">Tenor</MenuItem>

                              <MenuItem value="Baritone">Baritone</MenuItem>

                              <MenuItem value="Bass">Bass</MenuItem>

                            </Select>

                          </FormControl>

                        </Grid>

                        <Grid item xs={12} sm={6}>

                          <TextField

                            label="Vocal Range (e.g., E3–C6)"

                            value={formData.vocalRange}

                            onChange={(e) => setFormData(prev => ({ ...prev, vocalRange: e.target.value }))}

                            fullWidth

                          />

                        </Grid>

                      </Grid>

                    </AccordionDetails>

                  </Accordion>

                </Grid>



                {/* Removed Key Dialogue to declutter the sheet */}

              </Grid>

            </Grid>

            

            {/* Right column - Additional details */}

            <Grid item xs={12} md={6}>

              <Typography variant="subtitle1" fontWeight="500" gutterBottom>

                Additional Information

              </Typography>

              

              <Grid container spacing={2}>

                <Grid item xs={12}>

                  <Box

                    sx={{

                      display: 'flex',

                      flexDirection: 'column',

                      alignItems: 'center',

                      p: 2,

                      mb: 2,

                      borderRadius: 1,

                      border: `1px dashed ${alpha(theme.palette.primary.main, 0.3)}`,

                      backgroundColor: alpha(theme.palette.background.paper, 0.5),

                      
                      position: 'relative',

                      overflow: 'hidden'

                    }}

                  >

                    {imagePreview ? (

                      <>

                      <Box sx={{ position: 'relative', width: '100%', height: 220, mb: 2, borderRadius: 1, overflow: 'hidden' }}>

                        <img

                          src={imagePreview}

                          alt="Headshot preview"

                          style={{

                            width: '100%',

                            height: '100%',

                            objectFit: 'cover',

                            objectPosition: `${formData.headshotPosX}% ${formData.headshotPosY}%`

                          }}

                        />

                        <IconButton

                          sx={{

                            position: 'absolute',

                            top: 8,

                            right: 8,

                            backgroundColor: alpha(theme.palette.background.paper, 0.7),

                            
                            '&:hover': {

                              backgroundColor: alpha(theme.palette.background.paper, 0.9)

                            }

                          }}

                          onClick={() => {

                            setImagePreview(null);

                            setFormData(prev => ({ ...prev, headshot: '' }));

                          }}

                        >

                          <DeleteIcon fontSize="small" />

                        </IconButton>

                      </Box>

                      <Grid container spacing={2} sx={{ width: '100%', mb: 1 }}>

                        <Grid item xs={12} sm={6}>

                          <Typography variant="caption" color="text.secondary">Horizontal focus</Typography>

                          <Slider

                            size="small"

                            value={formData.headshotPosX}

                            onChange={(_, v) => setFormData(prev => ({ ...prev, headshotPosX: Array.isArray(v) ? v[0] : v }))}

                            min={0}

                            max={100}

                            valueLabelDisplay="auto"

                          />

                        </Grid>

                        <Grid item xs={12} sm={6}>

                          <Typography variant="caption" color="text.secondary">Vertical focus</Typography>

                          <Slider

                            size="small"

                            value={formData.headshotPosY}

                            onChange={(_, v) => setFormData(prev => ({ ...prev, headshotPosY: Array.isArray(v) ? v[0] : v }))}

                            min={0}

                            max={100}

                            valueLabelDisplay="auto"

                          />

                        </Grid>

                      </Grid>

                      </>

                    ) : (

                      <Box sx={{ textAlign: 'center', mb: 2 }}>

                        <ImageIcon sx={{ fontSize: 60, color: alpha(theme.palette.text.secondary, 0.5), mb: 1 }} />

                        <Typography variant="body2" color="text.secondary">

                          No headshot uploaded

                        </Typography>

                      </Box>

                    )}

                    

                    <Button

                      component="label"

                      variant="outlined"

                      startIcon={<CloudUploadIcon />}

                      sx={{

                        borderRadius: 1,

                        borderColor: alpha(theme.palette.primary.main, 0.3),

                        '&:hover': {

                          borderColor: theme.palette.primary.main,

                          backgroundColor: alpha(theme.palette.primary.main, 0.04)

                        }

                      }}

                    >

                      Upload Headshot

                      <input

                        type="file"

                        accept="image/*"

                        hidden

                        onChange={handleHeadshotUpload}

                      />

                    </Button>

                  </Box>

                </Grid>

                

                <Grid item xs={12}>

                  <TextField

                    name="description"

                    label="Character Description"

                    value={formData.description}

                    onChange={handleChange}

                    multiline

                    rows={3}

                    fullWidth

                    placeholder="Enter a description of the character"

                    sx={{

                      '& .MuiOutlinedInput-root': {

                        
                        backgroundColor: alpha(theme.palette.background.paper, 0.5),

                        borderRadius: 1,

                        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,

                        transition: 'all 0.3s ease-in-out',

                        '&:hover': {

                          backgroundColor: alpha(theme.palette.background.paper, 0.7)

                        },

                        '&.Mui-focused': {

                          borderColor: alpha(theme.palette.primary.main, 0.3)

                        }

                      }

                    }}

                  />

                </Grid>

                

                <Grid item xs={12}>

                  <Typography variant="subtitle2" sx={{ mb: 1 }}>

                    Contact Information

                  </Typography>

                  <Grid container spacing={2}>

                    <Grid item xs={12} sm={6}>

                      <TextField

                        name="contactInfo.email"

                        label="Email"

                        value={formData.contactInfo.email}

                        onChange={handleChange}

                        fullWidth

                        type="email"

                        sx={{

                          '& .MuiOutlinedInput-root': {

                            
                            backgroundColor: alpha(theme.palette.background.paper, 0.5),

                            borderRadius: 1,

                            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,

                            transition: 'all 0.3s ease-in-out',

                            '&:hover': {

                              backgroundColor: alpha(theme.palette.background.paper, 0.7),

                              boxShadow: `0 4px 12px 0 ${alpha(theme.palette.common.black, 0.08)}`

                            },

                            '&.Mui-focused': {

                              boxShadow: `0 4px 12px 0 ${alpha(theme.palette.primary.main, 0.15)}`,

                              borderColor: alpha(theme.palette.primary.main, 0.3)

                            }

                          }

                        }}

                      />

                    </Grid>

                    <Grid item xs={12} sm={6}>

                      <TextField

                        name="contactInfo.phone"

                        label="Phone"

                        value={formData.contactInfo.phone}

                        onChange={handleChange}

                        fullWidth

                        sx={{

                          '& .MuiOutlinedInput-root': {

                            
                            backgroundColor: alpha(theme.palette.background.paper, 0.5),

                            borderRadius: 1,

                            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,

                            transition: 'all 0.3s ease-in-out',

                            '&:hover': {

                              backgroundColor: alpha(theme.palette.background.paper, 0.7),

                              boxShadow: `0 4px 12px 0 ${alpha(theme.palette.common.black, 0.08)}`

                            },

                            '&.Mui-focused': {

                              boxShadow: `0 4px 12px 0 ${alpha(theme.palette.primary.main, 0.15)}`,

                              borderColor: alpha(theme.palette.primary.main, 0.3)

                            }

                          }

                        }}

                      />

                    </Grid>

                  </Grid>

                </Grid>

                

                <Grid item xs={12}>

                  <TextField

                    name="notes"

                    label="Additional Notes"

                    value={formData.notes}

                    onChange={handleChange}

                    multiline

                    rows={2}

                    fullWidth

                    placeholder="Any additional notes about this cast member"

                    sx={{

                      '& .MuiOutlinedInput-root': {

                        
                        backgroundColor: alpha(theme.palette.background.paper, 0.5),

                        borderRadius: 1,

                        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,

                        transition: 'all 0.3s ease-in-out',

                        '&:hover': {

                          backgroundColor: alpha(theme.palette.background.paper, 0.7)

                        },

                        '&.Mui-focused': {

                          borderColor: alpha(theme.palette.primary.main, 0.3)

                        }

                      }

                    }}

                  />

                </Grid>



          </Grid>

          </Grid>

          </Grid>

        </DialogContent>

        <DialogActions sx={{ p: 2, pt: 0 }}>

          <Button 

            onClick={handleClose} 

            sx={{ 

              borderRadius: 1,

              
              backgroundColor: alpha(theme.palette.background.paper, 0.5),

              color: theme.palette.text.secondary,

              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,

              '&:hover': {

                backgroundColor: alpha(theme.palette.background.paper, 0.7),

                boxShadow: `0 4px 12px 0 ${alpha(theme.palette.common.black, 0.08)}`

              }

            }}

          >

            Cancel

          </Button>

          <Button 

            variant="contained"
            onClick={handleSubmit} 

            sx={{ 

              borderRadius: 1,

              textTransform: 'none'

            }}

          >

            {editCast ? 'Update' : 'Add'}

          </Button>

        </DialogActions>

      </Dialog>



      {/* Delete confirmation dialog */}

      <Dialog

        open={confirmDeleteOpen}

        onClose={closeDeleteConfirm}

        maxWidth="xs"

        fullWidth

      >

        <DialogTitle>Delete Cast Member</DialogTitle>

        <DialogContent>

          <Typography>

            Are you sure you want to delete

            {castToDelete?.name ? ` "${castToDelete.name}"` : ' this cast member'}?

            This action cannot be undone.

          </Typography>

        </DialogContent>

        <DialogActions>

          <Button onClick={closeDeleteConfirm}>Cancel</Button>

          <Button color="error" variant="contained" onClick={confirmDelete}>Delete</Button>

        </DialogActions>

      </Dialog>



      <Snackbar

        open={snackbar.open}

        autoHideDuration={6000}

        onClose={() => setSnackbar({ ...snackbar, open: false })}

        sx={{ 

          '& .MuiAlert-root': {

            
            backgroundColor: alpha(

              snackbar.severity === 'success' ? theme.palette.success.main : 

              snackbar.severity === 'error' ? theme.palette.error.main : 

              snackbar.severity === 'warning' ? theme.palette.warning.main : 

              theme.palette.info.main, 

              0.1

            ),

            borderRadius: 1,

            border: `1px solid ${alpha(

              snackbar.severity === 'success' ? theme.palette.success.main : 

              snackbar.severity === 'error' ? theme.palette.error.main : 

              snackbar.severity === 'warning' ? theme.palette.warning.main : 

              theme.palette.info.main,

              0.2

            )}`,

            boxShadow: `0 8px 32px 0 ${alpha(theme.palette.common.black, 0.1)}`

          }

        }}

      >

        <Alert

          onClose={() => setSnackbar({ ...snackbar, open: false })}

          severity={snackbar.severity}

          sx={{ 

            width: '100%',

            '& .MuiAlert-icon': {

              color: snackbar.severity === 'success' ? theme.palette.success.main : 

                    snackbar.severity === 'error' ? theme.palette.error.main : 

                    snackbar.severity === 'warning' ? theme.palette.warning.main : 

                    theme.palette.info.main

            }

          }}

        >

          {snackbar.message}

        </Alert>

      </Snackbar>
    </>
  );

}



export default Cast;

